import styled from '@emotion/styled';
import { Song, SongPreview } from '~/interfaces';
import { typography } from '~/modules/Elements/cssMixins';
import styles from '~/modules/GameEngine/Drawing/styles';

interface Props {
  width: number;
  height: number;
  songData: SongPreview | Song;
  children: React.ReactNode;
  background?: React.ReactNode;
}

export default function SongPage({ songData, background, width, height, children, ...restProps }: Props) {
  return (
    <FocusedSongContainer video={songData.video} width={width} height={height} {...restProps}>
      <BackgroundImage video={songData.video} blur />
      {background && <Background>{background}</Background>}

      <ContentLayer>
        {/* Esta parte es “decorativa” (títulos), no necesita interacción */}
        <HeaderOverlay>
          <SongTitle>{songData.title}</SongTitle>
          <br />
          <SongArtist>{songData.artist}</SongArtist>
          <br />
          {songData.author && (
            <SongCreator>
              by&nbsp;
              {songData.authorUrl ? <a href={songData.authorUrl}>{songData.author}</a> : songData.author}
            </SongCreator>
          )}
        </HeaderOverlay>

        {/* ✅ Zona interactiva: aquí viven PostGame, Settings, botones, etc */}
        <InteractiveLayer>{children}</InteractiveLayer>
      </ContentLayer>
    </FocusedSongContainer>
  );
}

const FocusedSongContainer = styled.div<{ video: string; width: number; height: number }>`
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
  top: 0;
  background: black;
  position: relative;

  /* ✅ IMPORTANTE: no mates el scroll global */
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  /* En desktop normalmente no hace falta scrollear, pero no molesta.
     Si quieres, lo puedes limitar con media query. */
`;

const Background = styled.div`
  z-index: 1;
  top: 0;
  left: 0;
  position: absolute;
  width: 100%;
  height: 100%;
`;

const BaseBackgroundImage = styled(Background) <{ blur: boolean; video: string }>`
  background-size: cover;
  background-position: center center;
  filter: ${(props) => (props.blur ? 'blur(0.5rem)' : 'none')};
`;

const BackgroundImage = (props: { blur: boolean; video: string }) => (
  <BaseBackgroundImage style={{ backgroundImage: `url('https://i3.ytimg.com/vi/${props.video}/hqdefault.jpg')` }} {...props} />
);

const ContentLayer = styled.div`
  z-index: 2;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  position: absolute;

  /* Overlay no bloquea interacción por defecto */
  pointer-events: none;
`;

/** ✅ Solo el header, no clicable */
const HeaderOverlay = styled.div`
  position: relative;
  margin: 0 auto;
  color: white;
  max-width: 80vw;
  pointer-events: none;
`;

/** ✅ Aquí re-activamos interacción */
const InteractiveLayer = styled.div`
  position: absolute;
  inset: 0;

  /* Esto “devuelve” los clicks/taps a tus componentes */
  pointer-events: auto;
`;

export const ContentElement = styled.span`
  background: rgba(0, 0, 0, 0.5);
  display: inline-block;
  backdrop-filter: blur(0.5rem);
  ${typography};
  padding: 0.5rem 2rem;
  margin: 0 0 1rem 0;
`;

const SongTitle = styled(ContentElement)`
  color: ${styles.colors.text.active};
  margin-top: 2rem;
  font-size: 4.5rem;

  @media (max-width: 640px) {
    font-size: clamp(20px, 6vw, 34px);
    margin-top: 1rem;
  }
`;

const SongArtist = styled(ContentElement)`
  font-size: 3.5rem;

  @media (max-width: 640px) {
    font-size: clamp(16px, 5vw, 26px);
  }
`;

const SongCreator = styled(ContentElement)`
  font-size: 2rem;

  @media (max-width: 640px) {
    font-size: clamp(12px, 3.5vw, 16px);
  }
`;